import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    // Checked first: a recently viewed render comes from the search route, which has no product id.
    if (recommendationsPerformed === 'true') {
      return;
    }

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    const url = intent === 'recently_viewed' ? this.#recentlyViewedUrl(productId, sectionId) : this.#recommendationsUrl(productId, sectionId, intent);

    // Nothing viewed yet (other than this product): hide quietly
    if (!url) {
      this.classList.add('hidden');
      return;
    }

    this.#fetchCached(url)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * @param {string} productId
   * @param {string} sectionId
   * @param {string | undefined} intent
   * @returns {string}
   */
  #recommendationsUrl(productId, sectionId, intent) {
    return `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
  }

  /**
   * Recently viewed products are rendered through the search route (same approach as the
   * predictive search's recently viewed list): `id:1 OR id:2` returns exactly those products,
   * and the section reads them from `search.results`.
   * @param {string} productId - The current product, which is left out
   * @param {string} sectionId
   * @returns {string | null} The section URL, or null when there's nothing to show
   */
  #recentlyViewedUrl(productId, sectionId) {
    const limit = Number(new URL(this.dataset.url ?? '', location.origin).searchParams.get('limit')) || 4;
    const ids = RecentlyViewed.getProducts()
      .filter((/** @type {string} */ id) => id !== productId)
      .slice(0, limit);

    if (ids.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  /**
   * Fetches a section URL and caches the result for future use
   * @param {string} url
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCached(url) {
    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
